import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { notifyError } from '../../utils/notifyError'
import { clearErrorFromBorderColor, scrollIntoView, showErrorAsBorderColor, SurveyQuestionType } from '../../models/Survey'
import { type ISurveySection } from '../../models/SurveyTemplate'
import { type UseSurveyFeatureStepperReturnValue, type ISection, type ShouldRenderSectionFn, BranchingOperation, type HandleChangeFn, type OnSubmitFn, type SectionItem, type HandleBackFn, type HandleNextFn, type SectionResponseItem } from './components/SurveyFeatureStepper/types'
import { buildAnswerFieldName, bulkParseModdedSectionIdForSectionItems, findOrigin, findOriginInBranchContext, MULTIPLE_CHOICE_SEPERATOR, transformSections } from './components/SurveyFeatureStepper/utils'
import { useStepper } from './components/SurveyFeatureStepper/useStepper'
import { useFormContext } from 'react-hook-form'
import { debounce } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import { getResponsesFromDraft, updateLocalDraft } from './stepperHelper'
import { FREE_RESPONSE_MAX_LENGTH } from './constants'

/**
 * Custom hook to manage survey stepper functionality with branching logic, progress tracking,
 * validation, and dynamic rendering of sections and items.
 *
 * This hook:
 * - Transforms raw survey section data into a nested, hierarchical format
 * - Tracks progress and user responses across steps
 * - Validates required survey questions
 * - Manages rendering logic for conditionally visible (branched) sections
 */
export const useSurveyFeatureStepper = (rawSections: ISurveySection[] | undefined, onSubmit: OnSubmitFn, explicitDraftKey?: string): UseSurveyFeatureStepperReturnValue => {
  // Local state to store transformed sections
  const [sections, setSections] = useState<ISection[]>([])
  // CR-213: holds the full answer field names (itemId + modded section id) of invalid
  // questions, not bare itemIds, so a duplicate item flagged in one section is not also
  // highlighted in the other section that shares the same itemId.
  const [invalidFieldNames, setInvalidFieldNames] = useState<string[]>([])
  const [triggerPulse, setTriggerPulse] = useState<boolean>(false)

  // Refs to keep track of rendered sections and user responses without triggering re-renders
  const renderedSectionsRef = useRef<Map<string, boolean>>(new Map<string, boolean>())
  const responseItemsRef = useRef <Record<ISection['surveySectionId'], SectionResponseItem[]>>({})

  const { id } = useParams()
  const { handleSubmit, getValues } = useFormContext()
  const { step, setStep, setProgress } = useStepper()
  const draftKey = explicitDraftKey ?? (id ? `surveyDraft-${id}` : undefined)

  // CE-193: a survey arrives with submitted answers only when it is being reopened. Only
  // then do we retain explicit empty markers for cleared fields, so a deliberate clear is
  // not resurrected from the submitted answer on refresh. Fresh surveys keep the original
  // draft behavior (cleared fields are dropped), so non-reopen flows are unaffected.
  const hasSubmittedAnswers = useMemo(
    () => (rawSections ?? []).some(section =>
      section.surveyItems?.some(item => {
        const answer = item.answer
        return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
      })
    ),
    [rawSections]
  )

  /**
   * Transforms raw incoming sections data into a structured tree format using `transformSections`.
   */
  useEffect(() => {
    if (rawSections) {
      const transformed = transformSections(rawSections)
      const responses = draftKey ? getResponsesFromDraft(transformed, draftKey) : transformed
      setSections(responses)
    }
  }, [draftKey, rawSections])

  /**
   * Records survey items that have been rendered for the current step,
   * collecting user responses from the visible section tree.
   */
  const recordRenderedSectionItems = (): void => {
    const rootSection = sections[step]
    const items = getBranchItems(rootSection)

    responseItemsRef.current = { ...responseItemsRef.current, [rootSection.surveySectionId]: items }
  }

  /**
   * Recursively collects survey items for a section and its visible children.
   * Converts user inputs into response item format and handles multiselect splitting.
   */
  const getBranchItems = (section: ISection): SectionResponseItem[] => {
    const isRendered = renderedSectionsRef.current.has(section.surveySectionId)

    let surveyResponseItems = section.surveyItems.map(
      item => {
        const { itemId, value, revision, itemType } = item

        let answer: string | string[] | undefined = value === '' ? undefined : value

        if (itemType === SurveyQuestionType.MultiSelect && value !== '') {
          answer = value.split(MULTIPLE_CHOICE_SEPERATOR)
        }

        return { answer: isRendered ? answer : undefined, itemId, revision, instructorId: section.instructorId, surveySectionId: section.surveySectionId, surveySectionRevision: section.surveySectionRevision }
      }
    ) as SectionResponseItem[]

    for (const child of section.children) {
      const branchItems = getBranchItems(child)
      surveyResponseItems = [...surveyResponseItems, ...branchItems]
    }

    return surveyResponseItems
  }

  /**
   * Recursively validates required fields for a section and all visible child sections.
   * Returns lists of valid and invalid item IDs.
   * Uses React Hook Form's getValues() to get the current form values for accurate validation.
   */
  const validateBranchItems = (section: ISection): { invalidItems: string[], validItems: string[] } => {
    // Root sections (without parent) should always be validated, even if not yet marked as rendered
    // Child sections should only be validated if they are rendered (visible)
    if (section.parentSurveySectionId && !renderedSectionsRef.current.has(section.surveySectionId)) {
      return { invalidItems: [], validItems: [] }
    }

    let invalidItems: string[] = []
    let validItems: string[] = []

    section.surveyItems.forEach(
      item => {
        // CR-213: the field name is keyed by itemId + modded section id (section.surveySectionId
        // is already the modded id on transformed sections), so cross-section duplicates resolve
        // to their own value.
        const fieldName = buildAnswerFieldName(item.itemId, section.surveySectionId)

        // Get current value from React Hook Form (most up-to-date)
        const formValue = getValues(fieldName)

        // Determine the current value to validate
        let currentValue: string = ''
        if (formValue !== undefined && formValue !== null) {
          if (Array.isArray(formValue)) {
            // For MultiSelect, empty array means not filled
            currentValue = formValue.length > 0 ? formValue.join(MULTIPLE_CHOICE_SEPERATOR) : ''
          } else {
            currentValue = String(formValue)
          }
        } else {
          // Fall back to state value if form value is not available
          currentValue = item.value
        }

        let isInvalid = false

        // Check if required field is empty
        if (item.isRequired && (currentValue === '' || currentValue === null || currentValue === undefined)) {
          isInvalid = true
        }

        // Check character limit for FreeResponse
        if (item.itemType === SurveyQuestionType.FreeResponse && currentValue && currentValue.length > FREE_RESPONSE_MAX_LENGTH) {
          isInvalid = true
        }

        if (isInvalid) {
          invalidItems.push(fieldName)
        } else {
          validItems.push(fieldName)
        }
      }
    )

    for (const child of section.children) {
      const branchInfo = validateBranchItems(child)
      invalidItems = [...invalidItems, ...branchInfo.invalidItems]
      validItems = [...validItems, ...branchInfo.validItems]
    }

    return {
      invalidItems,
      validItems
    }
  }

  /**
   * Recursively calculates total and answered questions for a section and all its children.
   */
  const evaluateBranch = (section: ISection): { total: number, answered: number } => {
    let total = 0
    let answered = 0

    if (section.parentSurveySectionId && !renderedSectionsRef.current.has(section.surveySectionId)) { return { total, answered } }

    section.surveyItems.forEach(
      item => {
        total++
        if (item.value !== '') answered++
      }
    )

    section.children.forEach(child => {
      const childInfo = evaluateBranch(child)
      total += childInfo.total
      answered += childInfo.answered
    })

    return { total, answered }
  }

  /**
   * Evaluates progress across all root sections and updates progress.
   * Required for the progress bar state.
   */
  const evaluteProgress = (sections: ISection[]): void => {
    const progress = sections.reduce(
      (prev, root: ISection) => {
        const { total, answered } = evaluateBranch(root)

        const updatedProgress = { total: prev.total + total, answered: prev.answered += answered }
        return updatedProgress
      }, { total: 0, answered: 0 }
    )

    setProgress(progress)
  }

  /**
   * Debounced version of progress evaluation to prevent excessive computation on change.
   */
  const debouncedEvaluteProgress = useCallback(
    debounce((sections: ISection[]) => {
      evaluteProgress(sections)
    }, 1000), // 500ms debounce delay
    []
  )

  /**
   * Flattens and parses all response items for submission for backend.
   */
  const getResponseItems = (): SectionResponseItem[] => {
    return Object.values(responseItemsRef.current).reduce((p, c) => [...p, ...bulkParseModdedSectionIdForSectionItems(c)], [])
  }

  // Navigation Handlers

  /**
   * Moves the user one step back in the stepper.
   * Handles any state changes required when going one step back.
   */
  const handleBack: HandleBackFn = () => {
    setTriggerPulse(false)
    setStep(prev => prev - 1)
  }

  /**
   * Validates current step, shows errors if present, or progresses to next step.
   * On final step, submits the survey responses via `onSubmit`.
   */
  const handleNext: HandleNextFn = () => {
    const { validItems, invalidItems } = validateBranchItems(sections[step])

    setTriggerPulse(false)

    if (invalidItems.length === 0) {
      recordRenderedSectionItems()

      if (step < sections.length - 1) { setStep(prev => prev + 1) }

      if (step === sections.length - 1) {
        const onSubmitPromise = handleSubmit(async () =>
          await onSubmit(getResponseItems())
        )()
        onSubmitPromise.catch(notifyError)
      }

      return
    }

    // Show error styles for invalid items. CR-213: validateBranchItems already returns the
    // full answer field name (itemId + modded section id), which is the DOM element id, so we
    // look it up directly rather than re-deriving it from the current step's instructorId.
    invalidItems.forEach(
      (fieldName, index) => {
        const element = document.getElementById(fieldName)
        if (!element) {
          console.error('Found a question with field name {' + fieldName + '} without any HTMLElement')
          return
        }

        showErrorAsBorderColor(element)
        if (index === 0) scrollIntoView(element)
      }
    )

    // Clear error styles from valid items
    validItems.forEach(
      fieldName => {
        const element = document.getElementById(fieldName)
        if (!element) {
          console.error('Found a question with field name {' + fieldName + '} without any HTMLElement')
          return
        }
        clearErrorFromBorderColor(element)
      }
    )

    setInvalidFieldNames(invalidItems)
  }

  /**
   * Determines if a section should be rendered based on branching logic.
   */
  const shouldRenderSection: ShouldRenderSectionFn = (root, section) => {
    if (!section.origin?.itemId) return true

    const rootSection = sections.find(_section => _section.surveySectionId === root.surveySectionId)

    if (!rootSection) {
      console.error('Found a section without a root section: ' + section.surveySectionId)
      return false
    }
    const originItem = findOriginInBranchContext(rootSection, section) ?? findOrigin(rootSection, section.origin?.itemId)

    if (!originItem) {
      console.error('Found faulty data for origin: ' + section.surveySectionId)
      return false
    }

    const { itemType, branches, value } = originItem

    const branchingValues = branches
      .filter(b => b.sectionId === section.rawSurveySectionId)
      .map(branch => branch.branchingValue.toLowerCase().trim())
    if (branchingValues.length === 0) {
      console.error('Found a section with origin but no branch: ' + section.surveySectionId)

      return false
    }

    // Evaluate branching condition
    if (section.origin.operation.toLowerCase() === BranchingOperation.IS_EMPTY) return value === ''
    if (section.origin.operation.toLowerCase() === BranchingOperation.IS_NOT_EMPTY) return (value !== '')
    if (section.origin.operation.toLowerCase() === BranchingOperation.IS) {
      const formattedValue = value.toLowerCase().trim()

      if (itemType === SurveyQuestionType.LinearScale) return branchingValues.includes(formattedValue)
      if (itemType === SurveyQuestionType.MultipleChoice) return branchingValues.includes(formattedValue)
      if (itemType === SurveyQuestionType.MultiSelect) return !!branchingValues.find(branchingValue => formattedValue.split(MULTIPLE_CHOICE_SEPERATOR).includes(branchingValue))
    }

    return false
  }

  /**
   * Updates value of a specific survey item and triggers progress evaluation.
   */
  const handleChange: HandleChangeFn = (value, sectionId, itemId, name) => {
    const updateSection = (sections: ISection[]): ISection[] => {
      return sections.map(_section => {
        if (_section.surveySectionId === sectionId) {
          const surveyItems = _section.surveyItems.map(_item => {
            if (_item.itemId !== itemId) return _item

            const valueStr = typeof value === 'string' ? value : value.join(MULTIPLE_CHOICE_SEPERATOR)

            return { ..._item, value: valueStr }
          })
          return { ..._section, surveyItems }
        }

        if (_section.children.length > 0) {
          return { ..._section, children: updateSection(_section.children) }
        }
        return _section
      })
    }

    if (draftKey) {
      updateLocalDraft(name ?? '', value, draftKey, hasSubmittedAnswers)
    }
    setTriggerPulse(true)
    setSections(prev => {
      const updatedSections = updateSection(prev)
      debouncedEvaluteProgress(updatedSections)
      return updatedSections
    })
  }

  return { sections, renderedSectionsRef, invalidFieldNames, handleChange, handleBack, handleNext, shouldRenderSection, triggerPulse }
}
